import { fetchWithTimeout, readJsonResponse, responseError } from './apiRequest';

export const isExportSessionError = error => Boolean(error?.fatalExport);

function sessionError(code) {
  return Object.assign(new Error('Please sign in again before exporting.'), { code, fatalExport: true });
}

export function createExportSession(auth, initialSession, currentUserId, { fetchRead = fetchWithTimeout, now = Date.now } = {}) {
  const owner = initialSession?.user?.id;
  const controller = new AbortController();
  let refreshPromise;
  if (!owner || !initialSession?.access_token) throw sessionError('export_auth_required');

  const assertActive = () => {
    if (currentUserId() !== owner) {
      controller.abort();
      throw sessionError('export_account_changed');
    }
    if (controller.signal.aborted) throw sessionError('export_cancelled');
  };

  const refresh = async () => {
    assertActive();
    if (!refreshPromise) {
      refreshPromise = Promise.resolve().then(() => auth.refreshSession()).finally(() => { refreshPromise = undefined; });
    }
    let result;
    try { result = await refreshPromise; }
    catch { throw sessionError('export_auth_required'); }
    assertActive();
    const session = result?.data?.session;
    if (result?.error || !session?.access_token || session.user?.id !== owner) throw sessionError('export_auth_required');
    return session;
  };

  const getSession = async () => {
    assertActive();
    let result;
    try { result = await auth.getSession(); }
    catch { throw sessionError('export_auth_required'); }
    assertActive();
    const session = result?.data?.session;
    if (result?.error || !session?.access_token || session.user?.id !== owner) throw sessionError('export_auth_required');
    if (session.expires_at && session.expires_at * 1000 < now() + 120000) return refresh();
    return session;
  };

  const request = async (url, options = {}, timeout = 60000) => {
    for (let attempt = 0; attempt < 2; attempt++) {
      const session = await getSession();
      const pairs = options.headers?.entries ? [...options.headers.entries()] : Object.entries(options.headers || {});
      const headers = Object.fromEntries(pairs.filter(([key]) => key.toLowerCase() !== 'authorization'));
      headers.Authorization = `Bearer ${session.access_token}`;
      let response;
      try { response = await fetchRead(url, { ...options, signal: controller.signal, headers }, timeout); }
      catch (error) { assertActive(); throw error; }
      assertActive();
      if (![401, 403].includes(response.status)) return response;
      const payload = await readJsonResponse(response);
      assertActive();
      const authenticationFailed = response.status === 401 ||
        ['auth_required', 'invalid_token', 'session_expired', 'guest_session_expired'].includes(payload?.code) ||
        /^(Missing or invalid Authorization header|Authentication failed\.|Please sign in again)/i.test(String(payload?.error || ''));
      if (!authenticationFailed) throw Object.assign(responseError(response, payload, 'Export access denied'), { fatalExport: true });
      if (attempt) throw sessionError('export_auth_required');
      await refresh();
    }
    throw sessionError('export_auth_required');
  };
  return { request, assertActive, getSession, signal: controller.signal, cancel: () => controller.abort() };
}
